export function isEstimateLocked(estado) {
  return String(estado || "") !== "Created";
}

export function buildCopyPresupuestoNombre(nombre) {
  const base = String(nombre || "").trim();
  return base ? `${base} (Copy)` : "(Copy)";
}

export function buildNombrePresupuestoFromProyecto(proyectoSubject, numeroPresupuesto) {
  return `${String(proyectoSubject || "").trim()} - ${String(numeroPresupuesto || "").trim()}`;
}

export function normalizeCatalogName(value) {
  return String(value || "").trim().toLowerCase();
}

export function findCatalogItem(
  catalog = [],
  { idProducto, idMuestra, nombre } = {},
) {
  const pid = Number(idProducto);
  const mid = Number(idMuestra);

  const matchByIdAndTipo = (id, tipoEsperado) =>
    catalog.find((i) => {
      const sameId = Number(i.id ?? i.Id) === Number(id);
      if (!sameId) return false;
      if (!tipoEsperado) return true;
      return String(i.tipo ?? i.Tipo ?? "").toLowerCase() === tipoEsperado;
    });

  if (Number.isFinite(pid) && pid > 0) {
    const byProduct =
      matchByIdAndTipo(pid, "producto") || matchByIdAndTipo(pid, null);
    if (byProduct) return byProduct;
  }
  if (Number.isFinite(mid) && mid > 0) {
    const bySample =
      matchByIdAndTipo(mid, "muestra") || matchByIdAndTipo(mid, null);
    if (bySample) return bySample;
  }

  const normalizedName = normalizeCatalogName(nombre);
  if (!normalizedName) return null;

  return (
    catalog.find((i) => {
      const itemName = normalizeCatalogName(i.Nombre ?? i.nombre);
      return itemName === normalizedName;
    }) || null
  );
}

function hasPresupuestoVol(vol) {
  return vol !== null && vol !== undefined && String(vol).trim() !== "";
}

export { hasPresupuestoVol };

function pickIdentityValue(primary, fallback) {
  const primaryText = String(primary ?? "").trim();
  if (primaryText) return primary;
  return fallback ?? null;
}

/** Lee Type/Code/VOL del catálogo productos-muestras y completa desde VolumenPrenda (modelosTipo). */
export function getProductIdentityFields(item, modelosTipo = []) {
  if (!item) {
    return {
      TipoPrenda: null,
      ABREVIATURA: null,
      VOL: null,
      IdProducto: null,
      IdMuestra: null,
    };
  }

  const tipo = String(item.tipo ?? item.Tipo ?? "").toLowerCase();
  const itemId = item.id ?? item.Id ?? null;

  let TipoPrenda = item.TipoPrenda ?? item.tipoprenda ?? null;
  let ABREVIATURA = item.ABREVIATURA ?? item.abreviatura ?? null;
  let VOL = item.VOL ?? item.vol ?? null;

  const needsModelo =
    !hasPresupuestoVol(VOL) ||
    !String(TipoPrenda || "").trim() ||
    !String(ABREVIATURA || "").trim();

  if (needsModelo && modelosTipo.length) {
    const tipoPrendaId = Number(
      item.tipo_prenda ?? item.TipoPrendaId ?? item.ID_TIPO ?? item.idTipo,
    );
    const byId =
      Number.isFinite(tipoPrendaId) && tipoPrendaId > 0
        ? modelosTipo.find((m) => Number(m.ID_TIPO) === tipoPrendaId)
        : null;
    const byAbbr =
      !byId && ABREVIATURA
        ? modelosTipo.find(
            (m) => String(m.ABREVIATURA) === String(ABREVIATURA),
          )
        : null;
    const byTipo =
      !byId && !byAbbr && TipoPrenda
        ? modelosTipo.find(
            (m) =>
              String(m.TIPO || "").toLowerCase() ===
              String(TipoPrenda).toLowerCase(),
          )
        : null;
    const modelo = byId || byAbbr || byTipo;
    if (modelo) {
      TipoPrenda = TipoPrenda || modelo.TIPO || null;
      ABREVIATURA = ABREVIATURA || modelo.ABREVIATURA || null;
      if (!hasPresupuestoVol(VOL)) {
        VOL = modelo.VolumenUn ?? VOL ?? null;
      }
    }
  }

  return {
    TipoPrenda,
    ABREVIATURA,
    VOL,
    IdProducto: tipo === "producto" ? itemId : null,
    IdMuestra: tipo === "muestra" ? itemId : null,
  };
}

export function extractIdentityFromCatalogItem(item, modelosTipo = []) {
  return getProductIdentityFields(item, modelosTipo);
}

export function hasPresupuestoProductIdentity(presupuesto = {}) {
  const vol = presupuesto.VOL;
  const hasVol =
    vol !== null && vol !== undefined && String(vol).trim() !== "";
  return Boolean(presupuesto.TipoPrenda && presupuesto.ABREVIATURA && hasVol);
}

/** True si hay al menos Type, Code o Volume (arrastrar lo disponible aunque falte VOL). */
export function hasAnyPresupuestoProductIdentity(presupuesto = {}) {
  const vol = presupuesto.VOL;
  const hasVol =
    vol !== null && vol !== undefined && String(vol).trim() !== "";
  return Boolean(
    String(presupuesto.TipoPrenda || "").trim() ||
      String(presupuesto.ABREVIATURA || "").trim() ||
      hasVol,
  );
}

export function applyIdentityToPresupuesto(presupuesto = {}, identity = {}) {
  if (!identity) return presupuesto;
  return {
    ...presupuesto,
    TipoPrenda: identity.TipoPrenda ?? presupuesto.TipoPrenda ?? null,
    ABREVIATURA: identity.ABREVIATURA ?? presupuesto.ABREVIATURA ?? null,
    VOL: hasPresupuestoVol(presupuesto.VOL)
      ? presupuesto.VOL
      : (identity.VOL ?? presupuesto.VOL ?? null),
    IdProducto: presupuesto.IdProducto ?? identity.IdProducto ?? null,
    IdMuestra: presupuesto.IdMuestra ?? identity.IdMuestra ?? null,
  };
}

function parsePositiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function resolveProveedorIdFromCatalog(item, proveedores = []) {
  const direct =
    parsePositiveId(item?.IdProveedor) ||
    parsePositiveId(item?.CODPROV) ||
    null;
  if (direct) return direct;

  const raw = String(
    item?.ProveedorNombre ?? item?.PROVEEDOR ?? item?.Proveedor ?? "",
  ).trim();
  if (!raw || !proveedores.length) return null;

  const byCode = proveedores.find(
    (p) => String(p.CODPROV) === raw || Number(p.CODPROV) === Number(raw),
  );
  if (byCode) return byCode.CODPROV;

  const normalized = normalizeCatalogName(raw);
  const byName = proveedores.find(
    (p) => normalizeCatalogName(p.N_PROVEEDOR) === normalized,
  );
  return byName?.CODPROV ?? null;
}

/**
 * Supplier / Season / Material desde el Sample/Design del catálogo.
 * Solo devuelve campos con valor (no pisa con null).
 */
export function getProductSupplyFields(item, proveedores = []) {
  if (!item) return {};

  const temporadaId =
    parsePositiveId(item.TemporadaId) ||
    parsePositiveId(item.ID_Temporadas) ||
    parsePositiveId(item.ID_Temporada) ||
    null;

  const idSubgrupo =
    parsePositiveId(item.IdSubgrupo) ||
    parsePositiveId(item.main_fabric) ||
    parsePositiveId(item.ID_SUBGRUPO) ||
    parsePositiveId(item.DetalleAdicional) ||
    null;

  const idProveedor = resolveProveedorIdFromCatalog(item, proveedores);

  const out = {};
  if (temporadaId != null) out.TemporadaId = temporadaId;
  if (idSubgrupo != null) out.IdSubgrupo = idSubgrupo;
  if (idProveedor != null) out.IdProveedor = idProveedor;
  return out;
}

export function applySupplyToPresupuesto(
  presupuesto = {},
  item,
  proveedores = [],
) {
  return {
    ...presupuesto,
    ...getProductSupplyFields(item, proveedores),
  };
}

export function resolvePresupuestoProductIdentity(
  presupuesto = {},
  catalog = [],
  modelosTipo = [],
) {
  const item = findCatalogItem(catalog, {
    idProducto: presupuesto.IdProducto,
    idMuestra: presupuesto.IdMuestra,
    nombre: presupuesto.DiseñoMuestra,
  });
  const fromItem = getProductIdentityFields(item, modelosTipo);

  return {
    TipoPrenda: pickIdentityValue(presupuesto.TipoPrenda, fromItem.TipoPrenda),
    ABREVIATURA: pickIdentityValue(
      presupuesto.ABREVIATURA,
      fromItem.ABREVIATURA,
    ),
    VOL: hasPresupuestoVol(presupuesto.VOL)
      ? presupuesto.VOL
      : (fromItem.VOL ?? null),
  };
}

export function buildPresupuestoIdentityPatch(
  presupuesto = {},
  catalog = [],
  modelosTipo = [],
) {
  return resolvePresupuestoProductIdentity(presupuesto, catalog, modelosTipo);
}

export function mergePresupuestoWithCatalogItem(
  presupuesto = {},
  item,
  modelosTipo = [],
) {
  const fromItem = getProductIdentityFields(item, modelosTipo);

  return {
    ...presupuesto,
    TipoPrenda: pickIdentityValue(presupuesto.TipoPrenda, fromItem.TipoPrenda),
    ABREVIATURA: pickIdentityValue(
      presupuesto.ABREVIATURA,
      fromItem.ABREVIATURA,
    ),
    VOL: hasPresupuestoVol(presupuesto.VOL)
      ? presupuesto.VOL
      : (fromItem.VOL ?? null),
    IdProducto: presupuesto.IdProducto ?? fromItem.IdProducto ?? null,
    IdMuestra: presupuesto.IdMuestra ?? fromItem.IdMuestra ?? null,
  };
}
